import { useLayoutEffect, useState } from "react";
import type { PillContent } from "./compact-cursor-pill-content";

export const PILL_MAX_WIDTH = 340;
export const PILL_MAX_HEIGHT = 220;
export const PILL_MIN_HEIGHT = 40;
export const PILL_TEXT_LEFT = 14;
const PILL_VERTICAL_PADDING = 20;
const REPLY_HEADROOM = 48;
const REPLY_WIDTH_STEP = 32;

export function compactPillReserve(
  content: PillContent | null,
  canOpen: boolean,
  canDismiss: boolean,
): number {
  if (content?.kind === "question") return 24;
  if (content?.kind === "reply") {
    return content.terminal && canOpen ? 36 : 24;
  }
  return content?.kind === "error" && canDismiss ? 24 : 12;
}

export type CompactPillBox = {
  width: number;
  height: number;
  contentTop: number;
  contentHeight: number;
  radius: number;
};

/** Spare text space absorbs arriving words; buckets avoid resizing per token. */
export function bufferedReplyWidth(
  naturalWidth: number,
  reserve: number,
): number {
  return Math.min(
    PILL_MAX_WIDTH - PILL_TEXT_LEFT - reserve,
    Math.ceil((naturalWidth + REPLY_HEADROOM) / REPLY_WIDTH_STEP) *
      REPLY_WIDTH_STEP,
  );
}

export function compactPillBox(
  lineWidth: number,
  contentHeight: number,
  reserve: number,
  maxHeight = PILL_MAX_HEIGHT,
): CompactPillBox {
  const width = Math.min(
    PILL_MAX_WIDTH,
    Math.max(36, Math.ceil(lineWidth + PILL_TEXT_LEFT + reserve)),
  );
  const height = Math.min(
    maxHeight,
    Math.max(PILL_MIN_HEIGHT, Math.ceil(contentHeight + PILL_VERTICAL_PADDING)),
  );
  return {
    width,
    height,
    contentTop: Math.max(
      PILL_VERTICAL_PADDING / 2,
      (height - contentHeight) / 2,
    ),
    contentHeight,
    radius: Math.min(24, height / 2),
  };
}

/** Measures an invisible target clone so the visible pill can morph in place.
 *  The nodes are held in state: a pill that mounts again (hidden between
 *  steps, then shown with the same text) measures each node it attaches,
 *  before its first visible frame paints. */
export function useCompactPillMeasure(
  contentKey: string,
  reserve: number,
  fixedContentWidth?: number,
  bufferedReply = false,
) {
  const [clone, cloneRef] = useState<HTMLDivElement | null>(null);
  const [live, liveRef] = useState<HTMLDivElement | null>(null);
  const [box, setBox] = useState<CompactPillBox>(() =>
    compactPillBox(80, 16, reserve),
  );

  useLayoutEffect(() => {
    if (!clone) return;
    const measure = () => {
      // Pick the column once, then measure its final wrapping. Glyph-edge
      // fitting would shrink balanced text and give the live copy new breaks.
      clone.style.width = fixedContentWidth
        ? `${fixedContentWidth}px`
        : "max-content";
      const naturalWidth = Math.ceil(clone.getBoundingClientRect().width);
      const contentWidth =
        bufferedReply && !fixedContentWidth
          ? bufferedReplyWidth(naturalWidth, reserve)
          : naturalWidth;
      clone.style.width = `${contentWidth}px`;
      // Fixed-width composers can reveal local focus/dictation content that
      // their inert clone cannot own. Their live height cannot change wrapping.
      const liveHeight = fixedContentWidth
        ? (live?.getBoundingClientRect().height ?? 0)
        : 0;
      const contentHeight = Math.max(
        clone.getBoundingClientRect().height,
        liveHeight,
        16,
      );
      const maxHeight = fixedContentWidth
        ? Math.max(PILL_MIN_HEIGHT, window.innerHeight - 16)
        : PILL_MAX_HEIGHT;
      const next = compactPillBox(
        contentWidth,
        contentHeight,
        reserve,
        maxHeight,
      );
      setBox((previous) =>
        previous.width === next.width &&
        previous.height === next.height &&
        previous.contentHeight === next.contentHeight &&
        previous.contentTop === next.contentTop
          ? previous
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(clone);
    if (fixedContentWidth && live) observer.observe(live);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [clone, live, contentKey, reserve, fixedContentWidth, bufferedReply]);

  return { cloneRef, liveRef, box };
}
